import { ApolloError, useMutation } from '@apollo/client';
import { PlanningActionType, useConfig, usePlanning } from '@bechtel/client-common';
import { PLAN_WITH_AI } from '@bechtel/client-gql';
import {
  Box,
  Modal,
  ModalClose,
  ModalSize,
  Spinner,
  SpinnerSize,
  Text,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import { AiPlanningInput, AiPlanningPayload, PlanningErrorCode } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { FC } from 'react';
import {
  AiModalBodyStyled,
  AiModalFooterButtonPrimaryStyled,
  AiModalFooterButtonSecondaryStyled,
  AiModalFooterStyled,
  AiModalHeaderStyled,
} from './styled';

interface AiConfirmationModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AiConfirmationModal: FC<AiConfirmationModalProps> = ({ visible, onClose, onSuccess }) => {
  const { selectedForeman, selectedGeneralForeman, selectedSuperintendent, planningDispatch } = usePlanning();
  const dispatchToast = useToastsDispatch();
  const { numPlanningNextWeeks } = useConfig();

  const [planWithAi, { loading }] = useMutation<{ planAiPlanning: AiPlanningPayload }, { input: AiPlanningInput }>(
    PLAN_WITH_AI,
    {
      onCompleted: (data) => {
        if (data?.planAiPlanning?.planningAiRequestId && !data.planAiPlanning.error) {
          onSuccess?.();
          // Dispatch updated planningAiRequest so BacklogList re-fetches and server returns canPlan:false (locked) while AI is processing
          if (selectedForeman) {
            planningDispatch({
              type: PlanningActionType.CrewMemberSelected,
              payload: {
                selectedForeman: {
                  ...selectedForeman,
                  planningAiRequest: {
                    ...(selectedForeman.planningAiRequest || {}),
                    id: data.planAiPlanning.planningAiRequestId,
                    status: 'PROCESSING',
                    count: 0, // Initialize count to ensure state change triggers
                  },
                },
              },
            });
          }
          // Remove any existing toast before adding new one to prevent duplicates
          dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-planning-success' } });
          dispatchToast({
            type: 'ADD_TOAST',
            payload: {
              id: 'ai-planning-success',
              toastType: ToastType.INFO,
              content: () => (
                <>
                  <Spinner size={SpinnerSize.SMALL} />
                  <span style={{ marginLeft: '12px' }}>
                    <Trans>AI planning request submitted successfully. Please wait.</Trans>
                  </span>
                </>
              ),
            },
          });
          onClose();
        } else {
          dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-planning-error' } });
          dispatchToast({
            type: 'ADD_TOAST',
            payload: {
              id: 'ai-planning-error',
              toastType: ToastType.WARNING,
              content: () => (
                <Text size="500" weight="600">
                  {getErrorMessage(data?.planAiPlanning?.error, numPlanningNextWeeks)}
                </Text>
              ),
              autoDismissTimeout: 10000, // Auto-close after 10 seconds
            },
          });
          onClose();
        }
      },
      onError: (error) => {
        dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-planning-error' } });
        dispatchToast({
          type: 'ADD_TOAST',
          payload: {
            id: 'ai-planning-error',
            toastType: ToastType.WARNING,
            content: () => (
              <Text size="500" weight="600">
                {getErrorMessage(error, numPlanningNextWeeks)}
              </Text>
            ),
            autoDismissTimeout: 10000, // Auto-close after 10 seconds
          },
        });
        onClose();
      },
    }
  );

  const handleConfirm = () => {
    planWithAi({
      variables: {
        input: {
          foremanId: selectedForeman?.id || '',
          generalForemanId: selectedGeneralForeman?.id || '',
          superintendentId: selectedSuperintendent?.id || '',
        },
      },
    });
  };

  return (
    <Modal visible={visible} size={ModalSize.LARGE}>
      <AiModalHeaderStyled title={t`Confirm`}>
        <ModalClose onClick={onClose} />
      </AiModalHeaderStyled>
      <AiModalBodyStyled>
        <Box
          px={{ sm: '500' }}
          pt={{ sm: '700' }}
          pb={{ sm: '800' }}
          flexDirection={{ sm: 'row' }}
          alignItems={{ sm: 'flex-start' }}
        >
          <Text size="500" family="graphik" color="grey.1000" weight="600">
            {t`Please confirm to let AI schedule your milestones across the ${numPlanningNextWeeks === 3 ? 'Four' : 'Three'}-Week Look Ahead.`}
          </Text>
        </Box>
      </AiModalBodyStyled>
      <AiModalFooterStyled>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box mr={{ sm: '400' }}>
            <AiModalFooterButtonSecondaryStyled onClick={onClose}>
              <Trans>Cancel</Trans>
            </AiModalFooterButtonSecondaryStyled>
          </Box>
          <Box>
            <AiModalFooterButtonPrimaryStyled onClick={handleConfirm} disabled={loading}>
              {!loading && <Trans>Yes</Trans>}
              {loading && <Spinner />}
            </AiModalFooterButtonPrimaryStyled>
          </Box>
        </Box>
      </AiModalFooterStyled>
    </Modal>
  );
};

const getErrorMessage = (error: PlanningErrorCode | ApolloError | undefined, numPlanningNextWeeks: number) => {
  if (!error) return <Trans>An error has occurred. Please try again.</Trans>;
  if (error instanceof ApolloError) return <Trans>Network error has occurred. Please try again.</Trans>;
  switch (error) {
    case PlanningErrorCode.NO_MILESTONES_FOUND:
      return <Trans>No milestones found for planning. Please try again.</Trans>;
    case PlanningErrorCode.NO_WORKPACKAGES_SCHEDULED:
      return <Trans>No work packages assigned. Please try again.</Trans>;
    case PlanningErrorCode.PLANNING_API_ERROR:
      return <Trans>AI planning request failed due to a planning API error.</Trans>;
    case PlanningErrorCode.PLAN_WITH_AI_ERRORED:
      return <Trans>AI planning request failed. Please try again.</Trans>;
    case PlanningErrorCode.MISSING_REQUIRED_PERMISSION:
      return <Trans>AI planning request failed due to missing required permissions.</Trans>;
    case PlanningErrorCode.CREW_CAPACITY_ALREADY_PLANNED:
      return (
        <Trans>
          This crew has no available capacity for the {numPlanningNextWeeks === 3 ? 'Four' : 'Three'}-Week Look Ahead.
          All capacity is allocated to existing planned work.
        </Trans>
      );
    case PlanningErrorCode.PENDING_REQUEST_EXISTS:
      return (
        <Trans>A plan is already being generated. Please wait or cancel the existing plan before trying again.</Trans>
      );
    default:
      return <Trans>An error has occurred. Please try again.</Trans>;
  }
};
